import { FormSubmissionAnswerOutputData } from '@jobilla/nexus-sdk'
import ctl from '@netlify/classnames-template-literals'
import { CheckIcon } from 'lucide-react'
import { Ref } from 'react'
import { useTranslation } from 'react-i18next'

import { Tooltip } from '@/ui'
import type { ScoreBreakdownItem } from '@/ui'

export type ScoredOption = {
	id: string
	label: string
	scoreValue: number | null
	isDisqualifying: boolean | null
}

export const SCORE_LABEL_CONFIG: Record<number, { translationKey: string; colorClasses: string }> = {
	4: { translationKey: 'formBuilder.score.excellent', colorClasses: 'bg-green-100 text-green-700' },
	3: { translationKey: 'formBuilder.score.good', colorClasses: 'bg-lime-100 text-lime-700' },
	2: { translationKey: 'formBuilder.score.okay', colorClasses: 'bg-yellow-100 text-yellow-700' },
	1: { translationKey: 'formBuilder.score.poor', colorClasses: 'bg-amber-100 text-amber-700' },
	0: { translationKey: 'formBuilder.score.bad', colorClasses: 'bg-red-100 text-red-700' },
}

type Props = {
	questionNumber: number
	question: string
	answer: FormSubmissionAnswerOutputData[]
	options?: ScoredOption[]
	scoreContribution?: ScoreBreakdownItem
	isHighlighted?: boolean
	responseRef?: Ref<HTMLDivElement>
}

export function ApplicationResponseItem({
	questionNumber,
	question,
	answer,
	options,
	scoreContribution,
	isHighlighted,
	responseRef,
}: Props) {
	const { t } = useTranslation('common')
	const hasOptions = options && options.length > 0
	const selectedAnswers = hasOptions ? answer : []

	const optionsWithScoring = hasOptions
		? options.map((option) => {
			const isSelected = selectedAnswers.some(a => a.answer === option.label || a.optionId === option.id)
			const isDisqualifying = isSelected && option.isDisqualifying === true
			const scoreLabelConfig = option.scoreValue !== null ? SCORE_LABEL_CONFIG[option.scoreValue] : null
			return { ...option, isSelected, isDisqualifying, scoreLabelConfig }
		})
		: null

	return (
		<div
			ref={responseRef}
			className={ctl(`
				px-2 py-4 first:pt-0 transition-colors duration-700
				${isHighlighted ? 'bg-red-50 rounded-lg' : ''}
			`)}
		>
			<div className="flex items-start gap-2 mb-2">
				<span className="size-5 rounded-full bg-brand-100 text-brand-700 text-xs font-medium flex items-center justify-center shrink-0 mt-0.5">
					{questionNumber}
				</span>
				<p className="text-sm font-medium text-gray-700">{question}</p>
			</div>
			{optionsWithScoring
				? (
					<div className="ph-no-capture flex flex-col gap-1 ml-7">
						{optionsWithScoring.map((option) => (
							<div
								key={option.id}
								className={ctl(`
									inline-flex items-center gap-2 text-sm px-2 py-1 rounded
									${
									option.isDisqualifying
										? 'bg-red-50 text-red-700 font-medium'
										: option.isSelected
										? 'bg-brand-50 text-brand-700 font-medium'
										: 'text-gray-400'
								}
								`)}
								style={{ width: 'fit-content' }}
							>
								{option.isSelected
									? (
										<CheckIcon
											className={`size-3.5 shrink-0 ${
												option.isDisqualifying ? 'text-red-500' : 'text-brand-500'
											}`}
										/>
									)
									: <span className="size-3.5 rounded-full border border-gray-300 shrink-0" />}
								{option.label}
								{option.isSelected && option.isDisqualifying && (
									<span className="text-xs px-1.5 py-0.5 rounded font-medium bg-red-100 text-red-700">
										{t('editor:formBuilder.eligibility.disqualify')}
									</span>
								)}
								{option.isSelected && !option.isDisqualifying && option.scoreLabelConfig && (
									<span
										className={`text-xs px-1.5 py-0.5 rounded font-medium ${option.scoreLabelConfig.colorClasses}`}
									>
										{t(`editor:${option.scoreLabelConfig.translationKey}`)}
									</span>
								)}
							</div>
						))}
					</div>
				)
				: Array.isArray(answer)
				? (
					<div className="ph-no-capture flex flex-col gap-1 ml-7">
						{answer.map((answerItem, index) => (
							<div
								key={index}
								className="inline-flex items-center text-sm text-gray-600"
								style={{ width: 'fit-content' }}
							>
								<span className="mr-2 text-brand-400">→</span>
								{answerItem.answer}
							</div>
						))}
					</div>
				)
				: <div className="ph-no-capture text-sm text-gray-600 ml-7">{answer}</div>}
			{scoreContribution && (
				<div className="ml-7 mt-1.5 text-xs text-gray-400 inline-flex items-center gap-2">
					{t('candidateCard.scoring.contribution', {
						scored: scoreContribution.weightedScore,
						max: scoreContribution.maxScore,
					})}
					{scoreContribution.weight > 1 && (
						<span>
							{t('candidateCard.scoring.weightMultiplier', { weight: scoreContribution.weight })}
						</span>
					)}
					{scoreContribution.weightedScore === scoreContribution.maxScore && scoreContribution.rawScore < 4
						&& scoreContribution.maxScore > 0 && (
						<Tooltip content={t('candidateCard.scoring.maxScoreExplanation')}>
							<span className="text-gray-400 hover:text-gray-600 cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
								{t('candidateCard.scoring.why')}
							</span>
						</Tooltip>
					)}
				</div>
			)}
		</div>
	)
}
